import React, { useContext, useEffect, useState, lazy, Suspense } from "react";
import {
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
} from "@material-ui/core";
import { AgentBanner, useDeviceTypes } from "halifax";
import { IntlProvider } from "react-intl";
import { Provider, useDispatch } from "react-redux";
import { Route, Router, Switch } from "react-router-dom";
import {
  TripsModuleProps,
  TripsClientAssetProps,
  SessionInfo,
  CorpSessionInfo,
} from "redmond";
import { isCorpTenant, isCaponeTenant, UserSourceProvider } from "@capone/common";
import { useMedalliaFeedback } from "b2b-base/src/components/MedalliaFeedback";

import ExperimentsProvider, {
  ActiveExperiments,
  useExperiments,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  HOTEL_CFAR_REFUND_DISPLAY_VARIANTS,
  CHFAR_VARIANTS,
  AVAILABLE,
  CONTROL,
  DefaultVariantType,
} from "./context/experiments";
import useWindowEventListener from "./hooks/useWindowEventListener";
import { eventsToListen } from "./utils/events";
import {
  PATH_HOME,
  PATH_DISRUPTION_PROTECTION_OVERVIEW,
  PATH_CHANGE_FOR_ANY_REASON,
} from "./utils/paths";
import * as frenchTranslations from "./lang/fr.json";
import * as englishTranslations from "./lang/en.json";
import { store } from "./store";
import { TripsList, DisruptionProtection } from "./pages";
import AxiosInterceptors from "./components/AxiosInterceptors";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RewardsBanner } from "./modules/rewards/components";
import fetchUserInfo from "./api/v1/user/fetchUserInfo";
import { fetchCobtActiveExperiments } from "./api/v1/experiments/fetchCobtActiveExperiments";
import { setDisruptionProtectionExperiments } from "./pages/DisruptionProtection/actions/actions";
import "./App.scss";
import { setTripsListExperiments } from "./pages/TripsList/actions/actions";
import { TravelerModalWrapper } from "./pages/common";
import { config } from "./api/config";
import { setExperiments as setChfarExperiments } from "./features/change-for-any-reason/reducers/experiments/slice";

const ChangeForAnyReason = lazy(
  () => import("./pages/ChangeForAnyReason/component")
);

function loadLocaleData(locale: string): any {
  switch (locale) {
    case "fr":
      return frenchTranslations;
    default:
      return englishTranslations;
  }
}
const generateClassName = createGenerateClassName({
  productionPrefix: "ptTripsModule",
  seed: "ptTripsModule",
});

export const ClientContext = React.createContext<
  Partial<TripsClientAssetProps>
>({});

interface ITripsAppProps extends TripsModuleProps {
  showCoreHomepage?: boolean;
}

const App = (props: ITripsAppProps) => {
  const {
    baseHistory,
    clientAssets,
    experiments,
    isAgentPortal,
    language,
    showCoreHomepage,
    theme,
  } = props;
  const { matchesMobile } = useDeviceTypes();
  const [activeTheme, setActiveTheme] = useState(theme);
  const [locale, setLocale] = useState(language);
  const [messages, setMessages] = useState(loadLocaleData(locale).default);
  const [sessionInfo, setSessionInfo] = useState(clientAssets.sessionInfo);

  useEffect(() => {
    // note: it connects Cypress with the Redux store for testing
    if ((window as any).Cypress) {
      (window as any).store = store;
    }

    const fetchUser = async () => {
      try {
        const userInfoResponse = await fetchUserInfo();
        setSessionInfo(userInfoResponse);
      } catch (error) {
        console.error(error);
      }
    };
    fetchUser();
  }, []);

  const handleThemeChanged = (e: CustomEvent) => {
    setActiveTheme(e.detail);
    console.log("THEME RECEIVED:", e.detail.palette.type);
  };
  const handleLocaleChanged = (e: CustomEvent) => {
    setLocale(e.detail);
    const messages = loadLocaleData(e.detail);
    setMessages(messages.default);
  };
  useWindowEventListener(eventsToListen.HOST_THEME_CHANGED, handleThemeChanged);
  useWindowEventListener(
    eventsToListen.HOST_LOCALE_CHANGED,
    handleLocaleChanged
  );

  const { firstName, lastName } = sessionInfo?.userInfo || {
    firstName: "",
    lastName: "",
  };

  return (
    <Provider store={store}>
      <ExperimentsProvider initState={experiments}>
        <UserSourceProvider>
          <Router history={baseHistory}>
            <AxiosInterceptors isAgentPortal={isAgentPortal} />
            <ClientContext.Provider
              value={{ ...clientAssets, isAgentPortal, sessionInfo }}
            >
              <div className="App">
                <StylesProvider generateClassName={generateClassName}>
                  <ThemeProvider theme={activeTheme}>
                    {messages != null ? (
                      <IntlProvider
                        locale={locale}
                        defaultLocale="en"
                        messages={messages}
                      >
                        <Switch>
                          {isAgentPortal ? (
                            <Route path="*">
                              <AgentBanner
                                firstName={firstName}
                                lastName={lastName}
                              />
                            </Route>
                          ) : !showCoreHomepage ? (
                            <Route path="*">
                              <RewardsBanner />
                            </Route>
                          ) : null}
                        </Switch>
                        <Body
                          isMobile={matchesMobile}
                          sessionInfo={sessionInfo}
                          showCoreHomepage={showCoreHomepage}
                        />
                      </IntlProvider>
                    ) : (
                      <div>Loading</div>
                    )}
                  </ThemeProvider>
                </StylesProvider>
              </div>
            </ClientContext.Provider>
          </Router>
        </UserSourceProvider>
      </ExperimentsProvider>
    </Provider>
  );
};

const Body = (props: {
  isMobile: boolean;
  sessionInfo?: SessionInfo | CorpSessionInfo;
  showCoreHomepage?: boolean;
}) => {
  const { isMobile, sessionInfo, showCoreHomepage } = props;
  const dispatch = useDispatch();
  const expState = useExperiments();
  const { isConsumerMultiPortal } = useContext(ClientContext);
  const userRoles = isCorpTenant(config.TENANT)
    ? (sessionInfo as CorpSessionInfo).corporateInfo.role
    : [];

  // note: this useEffect helps to store some experiments in redux, so that some selectors can be created for unifying the gating logic
  useEffect(() => {
    dispatch(
      setDisruptionProtectionExperiments({
        [ActiveExperiments.DisruptionProtection24hRule]: getExperimentVariant(
          expState.experiments,
          ActiveExperiments.DisruptionProtection24hRule
        ),
        [ActiveExperiments.POST_BOOKING_OFFER_MY_TRIPS]: getExperimentVariant(
          expState.experiments,
          ActiveExperiments.POST_BOOKING_OFFER_MY_TRIPS
        ),
        [ActiveExperiments.FINTECH_CSAT]: getExperimentVariant(
          expState.experiments,
          ActiveExperiments.FINTECH_CSAT
        ),
      })
    );

    dispatch(
      setChfarExperiments({
        [ActiveExperiments.CHFAR]: getExperimentVariantCustomVariants(
          expState.experiments,
          ActiveExperiments.CHFAR,
          CHFAR_VARIANTS
        ),
      })
    );

    const regularCorpMyTripsVariant = getExperimentVariant(
      expState.experiments,
      ActiveExperiments.CORP_MYTRIPS_REPLATFORMING
    );
    // The trips replatform experiments (corp-mytrips-replatforming &
    // c1-trips-replatform-banner-notice) must resolve from the corporate
    // (COBT) LaunchDarkly project, not the consumer one, since a multi-portal
    // user can be segmented differently per portal. Only reach for the COBT
    // values on the capone tenant, only for users who can access the corporate
    // portal, and only when the consumer-project corp-mytrips-replatforming
    // flag is "available" — a kill switch that lets us disable the new logic
    // entirely from the consumer project if something goes wrong.
    const shouldUseCobtTripsExperiments =
      isCaponeTenant(config.TENANT) &&
      !!isConsumerMultiPortal &&
      regularCorpMyTripsVariant === AVAILABLE;

    const dispatchTripsListExperiments = (
      corpMyTripsVariant: DefaultVariantType,
      bannerNoticeVariant: DefaultVariantType
    ) =>
      dispatch(
        setTripsListExperiments({
          [ActiveExperiments.HOTEL_CFAR_REFUND_DISPLAY]:
            getExperimentVariantCustomVariants(
              expState.experiments,
              ActiveExperiments.HOTEL_CFAR_REFUND_DISPLAY,
              HOTEL_CFAR_REFUND_DISPLAY_VARIANTS
            ),
          [ActiveExperiments.FINTECH_CSAT]: getExperimentVariant(
            expState.experiments,
            ActiveExperiments.FINTECH_CSAT
          ),
          [ActiveExperiments.CORP_MYTRIPS_REPLATFORMING]: corpMyTripsVariant,
          [ActiveExperiments.TRIPS_REPLATFORM_BANNER_NOTICE]:
            bannerNoticeVariant,
        })
      );

    if (!shouldUseCobtTripsExperiments) {
      dispatchTripsListExperiments(CONTROL, CONTROL);
      return;
    }

    let isCancelled = false;
    fetchCobtActiveExperiments()
      .then((cobt) => {
        if (isCancelled) return;
        dispatchTripsListExperiments(
          getExperimentVariant(
            cobt.experiments,
            ActiveExperiments.CORP_MYTRIPS_REPLATFORMING
          ),
          getExperimentVariant(
            cobt.experiments,
            ActiveExperiments.TRIPS_REPLATFORM_BANNER_NOTICE
          )
        );
      })
      .catch(() => {
        if (isCancelled) return;
        // Fall back to control if the COBT call fails. getExperimentVariant
        // still honors a staging query-param override for either experiment.
        dispatchTripsListExperiments(
          getExperimentVariant(
            [],
            ActiveExperiments.CORP_MYTRIPS_REPLATFORMING
          ),
          getExperimentVariant(
            [],
            ActiveExperiments.TRIPS_REPLATFORM_BANNER_NOTICE
          )
        );
      });
    return () => {
      isCancelled = true;
    };
  }, [expState, isConsumerMultiPortal]);

  useMedalliaFeedback();

  return (
    <ErrorBoundary isMobile={isMobile}>
      <Route path={PATH_HOME} exact>
        <TripsList showCoreHomepage={showCoreHomepage} />
      </Route>
      <Route path={PATH_DISRUPTION_PROTECTION_OVERVIEW}>
        <DisruptionProtection />
      </Route>

      <Route path={PATH_CHANGE_FOR_ANY_REASON}>
        <Suspense>
          <ChangeForAnyReason />
        </Suspense>
      </Route>

      {isCorpTenant(config.TENANT) && (
        <TravelerModalWrapper isMobile={isMobile} userRoles={userRoles} />
      )}
    </ErrorBoundary>
  );
};

export default App;
